import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import TheField from "@/components/TheField";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const Chip = ({ label, active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 rounded-full text-sm border transition-all duration-300 ${active
      ? "border-primary bg-primary/15 text-foreground"
      : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}>
    {active && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}{label}
  </button>
);

export default function ReturnSetup() {
  const [opts, setOpts] = useState({ away_from: [], returning_to: [], focus_areas: [], physical: [] });
  const [step, setStep] = useState(0);
  const [away, setAway] = useState([]);
  const [returning, setReturning] = useState([]);
  const [focus, setFocus] = useState([]);
  const [physical, setPhysical] = useState("");
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);
  const [roadmap, setRoadmap] = useState("");
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  useEffect(() => { api.get("/return/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const steps = [
    { key: "intro" },
    { key: "away", title: "What's pulling you away?", sub: "Name the habits that take you out of yourself. Honesty is the first return.",
      options: opts.away_from, value: away, set: (v) => toggle(away, setAway, v), multi: true },
    { key: "returning", title: "What are you returning to?", sub: "The qualities that are already yours, waiting to come forward.",
      options: opts.returning_to, value: returning, set: (v) => toggle(returning, setReturning, v), multi: true },
    { key: "focus", title: "Where should we build first?", sub: "Your mindset foundations. We'll shape your practice around these.",
      options: opts.focus_areas, value: focus, set: (v) => toggle(focus, setFocus, v), multi: true },
    { key: "physical", title: "How do you want to move?", sub: "One physical anchor each morning to ground the nervous system.",
      options: opts.physical, value: physical ? [physical] : [], set: (v) => setPhysical(physical === v ? "" : v), multi: false },
    { key: "reflection" },
    { key: "result" },
  ];
  const s = steps[step];

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/return/setup", {
        away_from: away, returning_to: returning, focus_areas: focus, physical, reflection,
      });
      setRoadmap(data.setup.roadmap);
      await refreshUser?.();
      setStep(steps.length - 1);
    } catch { toast.error("Something interrupted your setup. Try again."); }
    finally { setSaving(false); }
  };

  const next = () => {
    if (s.key === "reflection") { submit(); return; }
    setStep((x) => Math.min(x + 1, steps.length - 1));
  };
  const canNext = s.multi ? s.value.length > 0 : true;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-2 mb-10">
          {steps.slice(1, -1).map((_, i) => (
            <span key={i} className={`h-1 rounded-full flex-1 transition-all duration-500 ${i < step ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={s.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
            {s.key === "intro" && (
              <div className="text-center flex flex-col items-center pt-6" data-testid="return-intro">
                <TheField intensity={0.7} size={180} active><span className="font-serif text-4xl text-foreground">R</span></TheField>
                <p className="text-xs uppercase tracking-[0.25em] text-primary mt-8 mb-3">Your Return</p>
                <h1 className="font-serif text-4xl sm:text-5xl mb-4">Let's build your way back.</h1>
                <p className="text-muted-foreground max-w-md leading-relaxed mb-8">A few honest questions, and Xcelio will shape a daily practice that pulls you out of the noise and back to who you actually are.</p>
                <Button size="lg" className="rounded-full" onClick={next} data-testid="return-begin-btn">Begin <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            )}

            {s.options && (
              <div>
                <h1 className="font-serif text-3xl sm:text-4xl mb-2">{s.title}</h1>
                <p className="text-muted-foreground mb-8 max-w-lg">{s.sub}</p>
                <div className="flex flex-wrap gap-2.5 mb-10">
                  {s.options.map((o) => (
                    <Chip key={o} label={o} active={s.value.includes(o)} onClick={() => s.set(o)} testid={`chip-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
                  ))}
                </div>
              </div>
            )}

            {s.key === "reflection" && (
              <div>
                <h1 className="font-serif text-3xl sm:text-4xl mb-2">One last thing.</h1>
                <p className="text-muted-foreground mb-6 max-w-lg">In your own words — who is the version of you on the other side of this? What does an ordinary day feel like for them?</p>
                <Textarea value={reflection} onChange={(e) => setReflection(e.target.value)} rows={6} data-testid="return-reflection"
                  placeholder="They wake up calm, not reaching for their phone. They speak without rehearsing. They…" className="resize-none rounded-2xl mb-10" />
              </div>
            )}

            {s.key === "result" && (
              <div className="text-center flex flex-col items-center pt-4" data-testid="return-result">
                <TheField intensity={0.85} size={150} active><Check className="w-8 h-8 text-primary" /></TheField>
                <p className="text-xs uppercase tracking-[0.25em] text-primary mt-8 mb-4">Your Return is set</p>
                <div className="glass rounded-2xl p-7 text-left max-w-xl mb-8">
                  <p className="text-foreground/90 leading-relaxed whitespace-pre-line font-serif text-lg">{roadmap}</p>
                </div>
                <Button size="lg" className="rounded-full" onClick={() => navigate("/routine")} data-testid="enter-routine-btn">
                  Enter my daily return <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {s.key !== "intro" && s.key !== "result" && (
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStep((x) => Math.max(0, x - 1))} data-testid="return-back-btn" className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button className="rounded-full" onClick={next} disabled={!canNext || saving} data-testid="return-next-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Shaping your return…</> : s.key === "reflection" ? "Create my return" : "Continue"}
              {!saving && s.key !== "reflection" && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
